import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTourPageFaqs } from '@/hooks/useTourPageFaqs';

export default function FAQSection() {
  const { faqs, loading, error, refresh } = useTourPageFaqs();
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string>('');

  useEffect(() => {
    if (faqs.length > 0 && !openId) {
      setOpenId(faqs[0].id);
    }
  }, [faqs, openId]);

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? '' : id));
  };

  return (
    <section className="w-full bg-background-50 py-8 md:py-12 lg:py-12">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <h2 className="mb-5 text-lg font-bold tracking-tight text-foreground-950 lg:mb-6 lg:text-[30px]">
          {t('faq.title')}
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <div className="h-12 w-full animate-pulse rounded-xl bg-background-100" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-background-200/70 bg-white py-12 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-question-line text-secondary-400 text-xl" />
            </div>
            <p className="text-sm text-secondary-500">{t('faq.error')}</p>
            <button
              type="button"
              onClick={refresh}
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-refresh-line" />
              {t('faq.tryAgain')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {faqs.map((faq) => (
              <div key={faq.id} className="flex flex-col gap-2">
                <div className="flex w-full flex-col gap-2">
                  <h3 className="flex">
                    <button
                      type="button"
                      aria-expanded={openId === faq.id}
                      onClick={() => toggle(faq.id)}
                      className="group flex flex-1 items-center justify-between gap-2 rounded-xl py-3 pl-3 pr-2 text-left text-[12px] font-bold leading-[1.3] tracking-wide transition-colors focus-visible:ring-0 bg-background-100 text-foreground-950 hover:bg-background-200/70 data-[state=open]:bg-primary-500 data-[state=open]:text-white"
                      data-state={openId === faq.id ? 'open' : 'closed'}
                    >
                      <span>{faq.question}</span>
                      <i
                        className={`ri-arrow-down-s-line text-base shrink-0 transition-transform duration-300 ${
                          openId === faq.id ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  </h3>
                  <div
                    className={`overflow-hidden text-sm transition-all duration-300 ${
                      openId === faq.id ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-2 pb-3 pt-0 text-[12px] leading-[1.5] tracking-wide text-secondary-500">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}